/**
 * ============================================================================
 * FAMILY MEMBER DETAIL v2.1.0 - TIME FORMAT FIX
 * ============================================================================
 *
 * Zeigt Details eines ausgewählten Familienmitglieds:
 * - Avatar, Name, Rolle, Alter, Arbeitsort
 * - Stats: Termine, Notizen, Einkäufe, Aufgaben
 * - Aktivitäts-Sektionen mit Listen
 *
 * ✅ v2.1.0: TIME FORMAT FIX
 *            - "00:00:00" wird nicht mehr angezeigt (ganztägige Events)
 *            - Zeit wird als HH:MM formatiert (ohne Sekunden)
 *
 * ✅ v2.0.0: HOVER EFFECTS
 *            - ActivityItem Komponente mit useState für Hover
 *            - Visuelle Hover-States (Hintergrund, Transform, Schatten)
 *            - Pfeil-Icon erscheint bei Hover
 *            - "Alle →" Button mit Hover-Effekt
 *
 * ✅ v1.9.0: KLICKBARE AKTIVITÄTEN
 *            - Einzelne Items klickbar → Navigation zum Modul
 *            - "Alle →" Button pro Sektion
 *            - onNavigate Callback für Modul-Wechsel
 *
 * ✅ v1.8.0: FIX SHOPPING FILTER - ID MATCH
 * ✅ v1.7.0: FIX FILTER FÜR NOTES/TASKS API
 * ✅ v1.6.0: "Dokumente" durch "Aufgaben" ersetzt (Tasks API)
 * ✅ v1.5.0: KORREKTE FILTER-LOGIK
 *
 * @version 2.1.0
 * @date 2026-01-21
 * ============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useToken } from '../../context/TokenContext';

console.log('📋 FamilyMemberDetail.js v2.1.0 loaded - TIME FORMAT FIX');

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Generiert Initialen aus dem Namen
 */
function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * ✅ v2.0.0: ActivityItem Komponente mit Hover-Effekt
 */
function ActivityItem({
  children,
  onClick,
  isDark,
  showArrow = true
}) {
  const [isHovered, setIsHovered] = useState(false);

  const colors = isDark ? {
    bg: '#374151',
    bgHover: '#4B5563',
    text: '#F9FAFB',
    textMuted: '#9CA3AF',
    border: '#4B5563',
    accent: '#4A90A4'
  } : {
    bg: '#E8F4F8',
    bgHover: '#D1E9F0',
    text: '#1F2937',
    textMuted: '#6B7280',
    border: '#B8D4DC',
    accent: '#4A90A4'
  };

  const itemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 10px',
    margin: '0 -8px 4px -8px',
    borderBottom: `1px solid ${colors.border}`,
    cursor: 'pointer',
    borderRadius: '10px',
    transition: 'all 0.2s ease',
    background: isHovered ? colors.bgHover : 'transparent',
    transform: isHovered ? 'translateX(4px)' : 'translateX(0)',
    boxShadow: isHovered ? '0 2px 8px rgba(74, 144, 164, 0.15)' : 'none'
  };

  const arrowStyle = {
    opacity: isHovered ? 1 : 0,
    transform: isHovered ? 'translateX(0)' : 'translateX(-8px)',
    transition: 'all 0.2s ease',
    color: colors.accent,
    fontSize: '1rem',
    marginLeft: '8px'
  };

  return (
    <div
      style={itemStyle}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
    >
      <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {children}
      </div>
      {showArrow && (
        <span style={arrowStyle}>→</span>
      )}
    </div>
  );
}

/**
 * FamilyMemberDetail Component
 *
 * @param {Object} member - Das ausgewählte Familienmitglied
 * @param {Function} onBack - Callback für Mobile "Zurück" Button
 * @param {Function} onEdit - Callback zum Bearbeiten des Mitglieds
 * @param {Function} onNavigate - Callback zur Navigation zu anderen Modulen (calendar, notes, shopping, tasks)
 */
function FamilyMemberDetail({
  member,
  onBack,
  onEdit,
  onNavigate
}) {
  const { isDark } = useTheme();
  const { token } = useToken();
  const styles = getStyles(isDark, member?.color);

  // Aktivitäten States
  const [termine, setTermine] = useState([]);
  const [notizen, setNotizen] = useState([]);
  const [einkaeufe, setEinkaeufe] = useState([]);
  const [aufgaben, setAufgaben] = useState([]);
  const [loading, setLoading] = useState(false);

  // Stats
  const stats = {
    termine: termine.length,
    notizen: notizen.length,
    einkaeufe: einkaeufe.length,
    aufgaben: aufgaben.length
  };

  /**
   * ✅ v1.2.0: Lädt Aktivitäten für das Mitglied
   * - Lädt alle Termine und filtert nach person ODER zeigt alle bei "alle"
   * - Lädt Einkaufslisten, die der Person zugewiesen sind
   * - Wartet auf gültigen Token bevor API-Calls gemacht werden
   */
  const loadActivities = useCallback(async () => {
    // ✅ v1.2.0: Robustere Token-Prüfung
    if (!member) return;
    if (!token || token.length < 10) {
      console.log('📋 Waiting for valid token before loading activities...');
      return;
    }

    setLoading(true);
    console.log('📋 Loading activities for:', member.name, '(token length:', token.length, ')');

    try {
      // ====================================================================
      // TERMINE LADEN - Alle laden und client-seitig filtern
      // ====================================================================
      const calendarRes = await fetch(
        `${API_BASE_URL}/api/calendar`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (calendarRes.status === 401) {
        console.warn('📅 Calendar API: 401 Unauthorized - token may be invalid');
      } else if (calendarRes.ok) {
        const calendarData = await calendarRes.json();
        const allAppointments = calendarData.appointments || [];
        const today = new Date().toISOString().split('T')[0];

        // ✅ v1.5.0: KORREKTE FILTER-LOGIK
        // - "alle" → Zeigt bei ALLEN Mitgliedern
        // - "" (leer) → Wird NICHT angezeigt
        // - "PersonName" → Zeigt NUR bei dieser Person
        const memberName = (member.name || '').toLowerCase().trim();
        const memberFirstName = memberName.split(' ')[0];

        console.log('📅 Filtering appointments for:', memberName, '(firstName:', memberFirstName, ')');
        console.log('📅 All appointments:', allAppointments.map(a => ({ title: a.title, person: a.person, date: a.date })));

        const memberTermine = allAppointments
          .filter(t => {
            // Zukünftige oder heutige Termine
            if (t.date < today) return false;

            // Prüfe ob Person übereinstimmt
            const appointmentPerson = (t.person || '').toLowerCase().trim();

            // 1. "alle" = für ALLE Mitglieder sichtbar ✅
            if (appointmentPerson === 'alle') {
              console.log(`  ✓ Appointment for ALL: "${t.title}"`);
              return true;
            }

            // 2. Leere Zuweisung = NICHT anzeigen ❌
            if (!appointmentPerson || appointmentPerson === '') {
              return false;
            }

            // 3. Namens-Match = nur für diese Person ✅
            const exactMatch = appointmentPerson === memberName;
            const firstNameMatch = appointmentPerson === memberFirstName;
            const partialMatch = memberFirstName.length >= 3 && (
              appointmentPerson.includes(memberFirstName) || memberFirstName.includes(appointmentPerson)
            );

            const matches = exactMatch || firstNameMatch || partialMatch;

            if (matches) {
              console.log(`  ✓ Appointment match: "${t.title}" (person: "${appointmentPerson}" vs member: "${memberName}")`);
            }

            return matches;
          })
          .sort((a, b) => {
            // Sortiere nach Datum, dann Zeit
            if (a.date !== b.date) return a.date.localeCompare(b.date);
            return (a.time || '').localeCompare(b.time || '');
          })
          .slice(0, 5);

        console.log(`📅 Found ${memberTermine.length} termine for ${member.name}`);
        setTermine(memberTermine);
      }

      // ====================================================================
      // EINKAUFSLISTEN LADEN - Alle laden und nach Person filtern
      // ====================================================================
      const shoppingRes = await fetch(
        `${API_BASE_URL}/api/shopping`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (shoppingRes.status === 401) {
        console.warn('🛒 Shopping API: 401 Unauthorized - token may be invalid');
      } else if (shoppingRes.ok) {
        const shoppingData = await shoppingRes.json();
        // ✅ v1.5.1: API gibt direkt Array zurück, nicht { lists: [...] }
        const allLists = Array.isArray(shoppingData) ? shoppingData : (shoppingData.lists || []);

        console.log('🛒 All shopping lists:', allLists.map(l => ({
          name: l.name,
          assigned_to: l.assigned_to,
          assignedTo: l.assignedTo,
          assigned_to_id: l.assigned_to_id
        })));
        console.log('🛒 Current member:', { id: member.id, name: member.name });

        // ✅ v1.8.0: KORREKTE FILTER-LOGIK FÜR SHOPPING API
        // Shopping API: assigned_to ist TEXT (kann ID oder Name sein!)
        // - "alle" → Zeigt bei ALLEN Mitgliedern
        // - "" (leer/null) → Zeigt bei ALLEN (wie "alle")
        // - "2" (ID als String) → Zeigt NUR bei Member mit id=2
        // - "PersonName" → Zeigt NUR bei dieser Person
        const memberName = (member.name || '').toLowerCase().trim();
        const memberId = String(member.id);
        const memberFirstName = memberName.split(' ')[0];

        const memberLists = allLists
          .filter(list => {
            // assigned_to kann String (ID oder Name) sein
            const rawAssignedTo = list.assigned_to ?? list.assignedTo ?? '';
            const assignedTo = String(rawAssignedTo).toLowerCase().trim();

            // DEBUG: Zeige jeden Vergleich
            console.log(`🔍 Shopping "${list.name}": assigned_to="${rawAssignedTo}" vs member id=${memberId} name="${memberName}"`);

            // 1. "alle" = für ALLE Mitglieder sichtbar ✅
            if (assignedTo === 'alle') {
              console.log(`  ✓ Shopping for ALL (assigned_to='alle'): "${list.name}"`);
              return true;
            }

            // 2. Leere Zuweisung = für ALLE sichtbar (wie "alle") ✅
            if (!assignedTo || assignedTo === '' || assignedTo === 'null' || assignedTo === 'undefined') {
              console.log(`  ✓ Shopping for ALL (empty assignment): "${list.name}"`);
              return true;
            }

            // 3. ID-Match: assigned_to ist die member.id ✅
            // (Shopping speichert member.id als String z.B. "2", "4")
            if (assignedTo === memberId) {
              console.log(`  ✓ Shopping ID match: "${list.name}" (${assignedTo} === ${memberId})`);
              return true;
            }

            // 4. Namens-Match = nur für diese Person ✅
            // Exakter Name-Match (für alte Daten die Namen statt IDs haben)
            const exactNameMatch = assignedTo === memberName;

            // Vorname-Match (nur wenn Vorname mindestens 3 Zeichen)
            const firstNameMatch = memberFirstName.length >= 3 && (
              assignedTo === memberFirstName ||
              assignedTo.includes(memberFirstName) ||
              memberFirstName.includes(assignedTo)
            );

            if (exactNameMatch || firstNameMatch) {
              console.log(`  ✓ Shopping name match: "${list.name}"`);
              return true;
            }

            console.log(`  ✗ No match for shopping: "${list.name}"`);
            return false;
          })
          .slice(0, 5);

        // Konvertiere zu Einkäufe-Format (zeige Listennamen mit Artikelanzahl)
        const einkaeufeItems = memberLists.map(list => ({
          id: list.id,
          name: list.name,
          quantity: list.item_count || list.items?.length || 0,
          unit: ' Artikel'
        }));

        console.log(`🛒 Found ${einkaeufeItems.length} shopping lists for ${member.name}`);
        setEinkaeufe(einkaeufeItems);
      }

      // ====================================================================
      // NOTIZEN LADEN - Alle laden und nach Person filtern
      // ====================================================================
      const notesRes = await fetch(
        `${API_BASE_URL}/api/notes`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (notesRes.status === 401) {
        console.warn('📝 Notes API: 401 Unauthorized - token may be invalid');
      } else if (notesRes.ok) {
        const notesData = await notesRes.json();
        // ✅ API gibt direkt Array zurück oder { notes: [...] }
        const allNotes = Array.isArray(notesData) ? notesData : (notesData.notes || []);

        console.log('📝 All notes:', allNotes.map(n => ({
          title: n.title,
          person: n.person,
          assignedTo: n.assignedTo,
          assigned_to: n.assigned_to
        })));
        console.log('📝 Current member:', { id: member.id, name: member.name });

        // ✅ v1.7.0: KORREKTE FILTER-LOGIK FÜR NOTES API
        // Notes API: assigned_to ist INTEGER (User-ID) oder NULL
        // NULL = für ALLE sichtbar (nicht "leer"!)
        // person-Feld kann auch String sein ("alle" oder Name)
        const memberId = String(member.id);
        const memberName = (member.name || '').toLowerCase().trim();
        const memberFirstName = memberName.split(' ')[0];

        const memberNotes = allNotes
          .filter(note => {
            // Prüfe beide mögliche Felder
            const noteAssignedToId = note.assigned_to ?? note.assignedTo;
            const notePerson = (note.person || '').toLowerCase().trim();

            // DEBUG
            console.log(`🔍 Checking note "${note.title}": assigned_to=${noteAssignedToId}, person="${notePerson}" vs member id=${memberId} name="${memberName}"`);

            // 1. NULL/undefined assigned_to = für ALLE sichtbar ✅
            // (Notes API speichert NULL für "alle", nicht den String "alle")
            if (noteAssignedToId === null || noteAssignedToId === undefined) {
              // Prüfe ob person-Feld explizit gesetzt ist
              if (!notePerson || notePerson === 'alle') {
                console.log(`  ✓ Note for ALL (assigned_to is null): "${note.title}"`);
                return true;
              }
            }

            // 2. person = "alle" String = für ALLE sichtbar ✅
            if (notePerson === 'alle') {
              console.log(`  ✓ Note for ALL (person='alle'): "${note.title}"`);
              return true;
            }

            // 3. ID-Match: assigned_to entspricht member.id ✅
            if (noteAssignedToId && String(noteAssignedToId) === memberId) {
              console.log(`  ✓ Note ID match: "${note.title}" (${noteAssignedToId} === ${memberId})`);
              return true;
            }

            // 4. Name-Match: person-Feld entspricht member.name
            if (notePerson) {
              const exactNameMatch = notePerson === memberName;
              const firstNameMatch = memberFirstName.length >= 3 && (
                notePerson === memberFirstName ||
                notePerson.includes(memberFirstName) ||
                memberFirstName.includes(notePerson)
              );

              if (exactNameMatch || firstNameMatch) {
                console.log(`  ✓ Note name match: "${note.title}"`);
                return true;
              }
            }

            console.log(`  ✗ No match for note: "${note.title}"`);
            return false;
          })
          .slice(0, 5);

        // Konvertiere zu Notizen-Format
        const notizenItems = memberNotes.map(note => ({
          id: note.id,
          title: note.title || 'Ohne Titel'
        }));

        console.log(`📝 Found ${notizenItems.length} notes for ${member.name}`);
        setNotizen(notizenItems);
      }

      // ====================================================================
      // AUFGABEN LADEN - Alle laden und nach Person filtern
      // ====================================================================
      const tasksRes = await fetch(
        `${API_BASE_URL}/api/tasks`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (tasksRes.status === 401) {
        console.warn('✅ Tasks API: 401 Unauthorized - token may be invalid');
      } else if (tasksRes.ok) {
        const tasksData = await tasksRes.json();
        // ✅ API gibt direkt Array zurück oder { tasks: [...] }
        const allTasks = Array.isArray(tasksData) ? tasksData : (tasksData.tasks || []);

        console.log('✅ All tasks:', allTasks.map(t => ({
          title: t.title,
          person: t.person,
          assigned_to: t.assigned_to,
          assignedTo: t.assignedTo,
          status: t.status
        })));
        console.log('✅ Current member:', { id: member.id, name: member.name });

        // ✅ v1.7.0: KORREKTE FILTER-LOGIK FÜR TASKS API
        // Tasks API: assigned_to ist INTEGER (User-ID) oder NULL
        // NULL = für ALLE sichtbar (nicht "leer"!)
        // person-Feld kann auch String sein ("alle" oder Name)
        const memberId = String(member.id);
        const memberName = (member.name || '').toLowerCase().trim();
        const memberFirstName = memberName.split(' ')[0];

        const memberTasks = allTasks
          .filter(task => {
            // Nur offene Aufgaben (nicht erledigt)
            if (task.status === 'completed' || task.status === 'done') {
              return false;
            }

            // Prüfe beide mögliche Felder
            const taskAssignedToId = task.assigned_to ?? task.assignedTo;
            const taskPerson = (task.person || '').toLowerCase().trim();

            // DEBUG
            console.log(`🔍 Checking task "${task.title}": assigned_to=${taskAssignedToId}, person="${taskPerson}" vs member id=${memberId} name="${memberName}"`);

            // 1. NULL/undefined assigned_to = für ALLE sichtbar ✅
            // (Tasks API speichert NULL für "alle", nicht den String "alle")
            if (taskAssignedToId === null || taskAssignedToId === undefined) {
              // Prüfe ob person-Feld explizit gesetzt ist
              if (!taskPerson || taskPerson === 'alle') {
                console.log(`  ✓ Task for ALL (assigned_to is null): "${task.title}"`);
                return true;
              }
            }

            // 2. person = "alle" String = für ALLE sichtbar ✅
            if (taskPerson === 'alle') {
              console.log(`  ✓ Task for ALL (person='alle'): "${task.title}"`);
              return true;
            }

            // 3. ID-Match: assigned_to entspricht member.id ✅
            if (taskAssignedToId && String(taskAssignedToId) === memberId) {
              console.log(`  ✓ Task ID match: "${task.title}" (${taskAssignedToId} === ${memberId})`);
              return true;
            }

            // 4. Name-Match: person-Feld entspricht member.name
            if (taskPerson) {
              const exactNameMatch = taskPerson === memberName;
              const firstNameMatch = memberFirstName.length >= 3 && (
                taskPerson === memberFirstName ||
                taskPerson.includes(memberFirstName) ||
                memberFirstName.includes(taskPerson)
              );

              if (exactNameMatch || firstNameMatch) {
                console.log(`  ✓ Task name match: "${task.title}"`);
                return true;
              }
            }

            console.log(`  ✗ No match for task: "${task.title}"`);
            return false;
          })
          .slice(0, 5);

        // Konvertiere zu Aufgaben-Format
        const aufgabenItems = memberTasks.map(task => ({
          id: task.id,
          title: task.title || 'Ohne Titel',
          dueDate: task.due_date || task.dueDate
        }));

        console.log(`✅ Found ${aufgabenItems.length} tasks for ${member.name}`);
        setAufgaben(aufgabenItems);
      }

    } catch (err) {
      console.error('❌ Error loading activities:', err);
    } finally {
      setLoading(false);
    }
  }, [member, token]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  if (!member) {
    return (
      <div style={styles.emptyContainer}>
        <div style={styles.emptyIcon}>👈</div>
        <h3 style={styles.emptyTitle}>Wähle ein Mitglied aus</h3>
        <p style={styles.emptyText}>
          Klicke auf ein Familienmitglied in der Liste, um Details zu sehen.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        {onBack && (
          <button
            style={styles.backButton}
            onClick={onBack}
            type="button"
          >
            ← Zurück
          </button>
        )}

        {onEdit && (
          <button
            style={styles.editButton}
            onClick={() => onEdit(member)}
            type="button"
            title="Bearbeiten"
          >
            ✏️
          </button>
        )}
      </div>

      {/* Profile Section */}
      <div style={styles.profile}>
        <div style={styles.avatar}>
          {member.avatar || getInitials(member.name)}
        </div>
        <div style={styles.profileInfo}>
          <h2 style={styles.name}>{member.name}</h2>
          <p style={styles.meta}>
            {member.role && <span>{member.role}</span>}
            {member.age && <span> • {member.age} Jahre</span>}
            {member.location && <span> • {member.location}</span>}
          </p>
          {member.schedule && (
            <p style={styles.schedule}>
              🕐 {member.schedule}
            </p>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <span style={styles.statValue}>{stats.termine}</span>
          <span style={styles.statLabel}>Termine</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statValue}>{stats.notizen}</span>
          <span style={styles.statLabel}>Notizen</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statValue}>{stats.einkaeufe}</span>
          <span style={styles.statLabel}>Einkäufe</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statValue}>{stats.aufgaben}</span>
          <span style={styles.statLabel}>Aufgaben</span>
        </div>
      </div>

      {/* Activities Sections */}
      <div style={styles.sections}>
        {/* Termine */}
        <ActivitySection
          title="📅 Termine"
          items={termine}
          loading={loading}
          moduleName="calendar"
          onItemClick={onNavigate}
          isDark={isDark}
          renderItem={(item) => {
            const timeDisplay = formatTime(item.time);
            const dateDisplay = item.date ? formatDate(item.date) : '';
            // Zeit + Datum anzeigen, Separator nur wenn beide vorhanden
            const metaText = timeDisplay && dateDisplay
              ? `${timeDisplay} • ${dateDisplay}`
              : timeDisplay || dateDisplay;
            return (
              <ActivityItem
                key={item.id}
                onClick={() => onNavigate?.(item, 'calendar')}
                isDark={isDark}
              >
                <span style={styles.activityTitle}>{item.title}</span>
                <span style={styles.activityMeta}>{metaText}</span>
              </ActivityItem>
            );
          }}
          emptyText="Keine anstehenden Termine"
        />

        {/* Notizen */}
        <ActivitySection
          title="📝 Notizen"
          items={notizen}
          loading={loading}
          moduleName="notes"
          onItemClick={onNavigate}
          isDark={isDark}
          renderItem={(item) => (
            <ActivityItem
              key={item.id}
              onClick={() => onNavigate?.(item, 'notes')}
              isDark={isDark}
            >
              <span style={styles.activityTitle}>{item.title}</span>
              <span style={styles.activityMeta}></span>
            </ActivityItem>
          )}
          emptyText="Keine Notizen"
        />

        {/* Einkäufe */}
        <ActivitySection
          title="🛒 Einkäufe"
          items={einkaeufe}
          loading={loading}
          moduleName="shopping"
          onItemClick={onNavigate}
          isDark={isDark}
          renderItem={(item) => (
            <ActivityItem
              key={item.id}
              onClick={() => onNavigate?.(item, 'shopping')}
              isDark={isDark}
            >
              <span style={styles.activityTitle}>{item.name}</span>
              <span style={styles.activityMeta}>
                {item.quantity}{item.unit} {item.price ? `• €${item.price.toFixed(2)}` : ''}
              </span>
            </ActivityItem>
          )}
          emptyText="Keine zugewiesenen Einkäufe"
        />

        {/* Aufgaben */}
        <ActivitySection
          title="✅ Aufgaben"
          items={aufgaben}
          loading={loading}
          moduleName="tasks"
          onItemClick={onNavigate}
          isDark={isDark}
          renderItem={(item) => (
            <ActivityItem
              key={item.id}
              onClick={() => onNavigate?.(item, 'tasks')}
              isDark={isDark}
            >
              <span style={styles.activityTitle}>{item.title}</span>
              <span style={styles.activityMeta}>
                {item.dueDate ? formatDate(item.dueDate) : ''}
              </span>
            </ActivityItem>
          )}
          emptyText="Keine offenen Aufgaben"
        />
      </div>
    </div>
  );
}

/**
 * ✅ v2.0.0: "Alle anzeigen" Button mit Hover-Effekt
 */
function ShowAllButton({ onClick, title, isDark }) {
  const [isHovered, setIsHovered] = useState(false);

  const style = {
    background: isHovered ? (isDark ? '#4B5563' : '#D1E9F0') : 'transparent',
    border: 'none',
    color: '#4A90A4',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    padding: '6px 12px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    transform: isHovered ? 'scale(1.05)' : 'scale(1)',
    boxShadow: isHovered ? '0 2px 6px rgba(74, 144, 164, 0.2)' : 'none'
  };

  return (
    <button
      style={style}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={title}
    >
      Alle →
    </button>
  );
}

/**
 * Activity Section Component
 *
 * @param {string} title - Titel der Sektion
 * @param {Array} items - Liste der Items
 * @param {boolean} loading - Ladezustand
 * @param {Function} renderItem - Render-Funktion für einzelne Items
 * @param {string} emptyText - Text wenn keine Items
 * @param {boolean} isDark - Dark Mode
 * @param {Function} onItemClick - Callback wenn ein Item geklickt wird
 * @param {string} moduleName - Name des Moduls für Navigation (calendar, notes, shopping, tasks)
 */
function ActivitySection({ title, items, loading, renderItem, emptyText, isDark, onItemClick, moduleName }) {
  const sectionStyles = getSectionStyles(isDark);

  return (
    <div style={sectionStyles.section}>
      <div style={sectionStyles.headerRow}>
        <h4 style={sectionStyles.title}>{title}</h4>
        {/* ✅ v2.0.0: "Alle anzeigen" Button mit Hover */}
        {!loading && items.length > 0 && onItemClick && (
          <ShowAllButton
            onClick={() => onItemClick(null, moduleName)}
            title={`Alle ${title.replace(/[📅📝🛒✅]/g, '').trim()} anzeigen`}
            isDark={isDark}
          />
        )}
      </div>
      <div style={sectionStyles.content}>
        {loading ? (
          <div style={sectionStyles.loading}>Lade...</div>
        ) : items.length === 0 ? (
          <div style={sectionStyles.empty}>{emptyText}</div>
        ) : (
          items.map(renderItem)
        )}
      </div>
    </div>
  );
}

/**
 * Format date helper
 */
function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

/**
 * Format time helper - versteckt "00:00:00" für ganztägige Events
 */
function formatTime(timeString) {
  if (!timeString) return '';
  // Ganztägige Events haben Zeit "00:00:00" oder "00:00" - diese nicht anzeigen
  if (timeString === '00:00:00' || timeString === '00:00') {
    return '';
  }
  // Format HH:MM (ohne Sekunden)
  return timeString.substring(0, 5);
}

/**
 * Section Styles
 */
function getSectionStyles(isDark) {
  const colors = isDark ? {
    bg: '#374151',
    text: '#F9FAFB',
    textMuted: '#9CA3AF',
    border: '#4B5563'
  } : {
    bg: '#E8F4F8',
    text: '#1F2937',
    textMuted: '#6B7280',
    border: '#B8D4DC'
  };

  return {
    section: {
      marginBottom: '16px'
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px'
    },
    title: {
      margin: 0,
      fontSize: '0.95rem',
      fontWeight: 600,
      color: colors.text
    },
    showAllBtn: {
      background: 'transparent',
      border: 'none',
      color: '#4A90A4',
      fontSize: '0.8rem',
      fontWeight: 500,
      cursor: 'pointer',
      padding: '4px 8px',
      borderRadius: '6px',
      transition: 'background 0.2s ease'
    },
    content: {
      background: colors.bg,
      borderRadius: '12px',
      border: `1px solid ${colors.border}`,
      padding: '12px'
    },
    loading: {
      color: colors.textMuted,
      fontSize: '0.85rem',
      textAlign: 'center',
      padding: '8px'
    },
    empty: {
      color: colors.textMuted,
      fontSize: '0.85rem',
      textAlign: 'center',
      padding: '8px'
    }
  };
}

/**
 * Main Styles
 */
function getStyles(isDark, memberColor) {
  const colors = isDark ? {
    bg: '#1F2937',
    bgSecondary: '#374151',
    text: '#F9FAFB',
    textSecondary: '#D1D5DB',
    textMuted: '#9CA3AF',
    border: '#4B5563',
    accent: '#4A90A4',
    accentLight: '#6BA893'
  } : {
    bg: '#FFFFFF',
    bgSecondary: '#E8F4F8',
    text: '#1F2937',
    textSecondary: '#374151',
    textMuted: '#6B7280',
    border: '#B8D4DC',
    accent: '#4A90A4',
    accentLight: '#6BA893'
  };

  const avatarColor = memberColor || '#4A90A4';

  return {
    container: {
      background: colors.bg,
      borderRadius: '16px',
      border: `2px solid ${colors.border}`,
      padding: '20px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'auto'
    },

    emptyContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: colors.bgSecondary,
      borderRadius: '16px',
      border: `2px dashed ${colors.border}`,
      padding: '40px 20px',
      height: '100%',
      textAlign: 'center'
    },

    emptyIcon: {
      fontSize: '4rem',
      marginBottom: '16px',
      opacity: 0.5
    },

    emptyTitle: {
      margin: '0 0 8px 0',
      fontSize: '1.2rem',
      fontWeight: 600,
      color: colors.text
    },

    emptyText: {
      margin: 0,
      fontSize: '0.9rem',
      color: colors.textMuted,
      maxWidth: '250px'
    },

    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px'
    },

    backButton: {
      padding: '8px 16px',
      background: colors.bgSecondary,
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      color: colors.text,
      fontSize: '0.9rem',
      cursor: 'pointer'
    },

    editButton: {
      padding: '8px 12px',
      background: colors.bgSecondary,
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      fontSize: '1rem',
      cursor: 'pointer'
    },

    profile: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      marginBottom: '20px',
      padding: '20px',
      background: colors.bgSecondary,
      borderRadius: '12px',
      border: `1px solid ${colors.border}`
    },

    avatar: {
      width: '72px',
      height: '72px',
      borderRadius: '16px',
      background: `linear-gradient(135deg, ${avatarColor}, ${adjustColor(avatarColor, 20)})`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      fontWeight: 700,
      color: 'white',
      flexShrink: 0
    },

    profileInfo: {
      flex: 1
    },

    name: {
      margin: '0 0 4px 0',
      fontSize: '1.4rem',
      fontWeight: 700,
      color: colors.text
    },

    meta: {
      margin: '0 0 4px 0',
      fontSize: '0.9rem',
      color: colors.textSecondary
    },

    schedule: {
      margin: 0,
      fontSize: '0.85rem',
      color: colors.textMuted
    },

    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '12px',
      marginBottom: '20px'
    },

    statCard: {
      background: colors.bgSecondary,
      border: `1px solid ${colors.border}`,
      borderRadius: '12px',
      padding: '12px',
      textAlign: 'center'
    },

    statValue: {
      display: 'block',
      fontSize: '1.5rem',
      fontWeight: 700,
      color: colors.accent
    },

    statLabel: {
      display: 'block',
      fontSize: '0.75rem',
      color: colors.textMuted,
      marginTop: '4px'
    },

    sections: {
      flex: 1,
      overflowY: 'auto'
    },

    activityItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 8px',
      margin: '0 -8px',
      borderBottom: `1px solid ${colors.border}`,
      cursor: 'pointer',
      borderRadius: '8px',
      transition: 'background 0.15s ease'
    },

    activityTitle: {
      fontSize: '0.9rem',
      color: colors.text,
      fontWeight: 500
    },

    activityMeta: {
      fontSize: '0.8rem',
      color: colors.textMuted
    }
  };
}

/**
 * Helper: Adjusts color brightness
 */
function adjustColor(hex, percent) {
  if (!hex) return '#6BA893';
  hex = hex.replace('#', '');

  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  r = Math.min(255, Math.max(0, r + (r * percent / 100)));
  g = Math.min(255, Math.max(0, g + (g * percent / 100)));
  b = Math.min(255, Math.max(0, b + (b * percent / 100)));

  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
}

export default FamilyMemberDetail;
